// Thin wrapper around the `composer-chat` edge function's non-streaming ops.
// The streaming chat turn goes through the AI SDK transport directly.
import { supabase } from "@/integrations/supabase/client";

export type ComposerChatThread = {
  id: string;
  piece_id: string;
  piece_title: string | null;
  user_id: string;
  author_display_name: string | null;
  is_mine: boolean;
  title: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  last_message_at: string | null;
  last_message_snippet: string | null;
  last_message_role: string | null;
  unread: boolean;
};

export type ComposerChatThreadDetail = ComposerChatThread & {
  workspace_id: string;
};

type Body = Record<string, unknown>;

async function invoke<T>(body: Body): Promise<T> {
  const { data, error } = await supabase.functions.invoke("composer-chat", { body });
  if (error) throw new Error(error.message);
  return data as T;
}

export const composerChatApi = {
  listThreads: (
    workspaceId: string,
    pieceId?: string | null,
    opts?: { scope?: "mine" | "all" },
  ) =>
    invoke<{ threads: ComposerChatThread[]; scope: "mine" | "all"; is_owner: boolean | null }>({
      op: "list_threads",
      workspace_id: workspaceId,
      ...(pieceId ? { piece_id: pieceId } : {}),
      ...(opts?.scope ? { scope: opts.scope } : {}),
    }),
  getThread: (workspaceId: string, threadId: string) =>
    invoke<{ thread: ComposerChatThreadDetail }>({
      op: "get_thread",
      workspace_id: workspaceId,
      thread_id: threadId,
    }),
  createThread: (workspaceId: string, pieceId: string, title?: string) =>
    invoke<{ thread: { id: string; title: string | null; created_at: string; updated_at: string } }>({
      op: "create_thread",
      workspace_id: workspaceId,
      piece_id: pieceId,
      ...(title ? { title } : {}),
    }),
  renameThread: (workspaceId: string, pieceId: string, threadId: string, title: string) =>
    invoke<{ ok: true }>({
      op: "rename_thread",
      workspace_id: workspaceId,
      piece_id: pieceId,
      thread_id: threadId,
      title,
    }),
  archiveThread: (workspaceId: string, pieceId: string, threadId: string) =>
    invoke<{ ok: true }>({
      op: "archive_thread",
      workspace_id: workspaceId,
      piece_id: pieceId,
      thread_id: threadId,
    }),
  unarchiveThread: (workspaceId: string, pieceId: string, threadId: string) =>
    invoke<{ ok: true }>({
      op: "unarchive_thread",
      workspace_id: workspaceId,
      piece_id: pieceId,
      thread_id: threadId,
    }),
  deleteThread: (workspaceId: string, pieceId: string, threadId: string) =>
    invoke<{ ok: true }>({
      op: "delete_thread",
      workspace_id: workspaceId,
      piece_id: pieceId,
      thread_id: threadId,
    }),
  markRead: (workspaceId: string, threadId: string) =>
    invoke<{ ok: true }>({
      op: "mark_read",
      workspace_id: workspaceId,
      thread_id: threadId,
    }),
};
